import type { ConsentTypeCode } from '@/lib/api/consents/types';
import { toggleConsentField, type ConsentFormValues } from './formValues';

export type ConsentFormState = {
  values: ConsentFormValues;
  mobilePhoneError: string | null;
};

export type ConsentFormAction =
  | { type: 'TOGGLE'; consentType: ConsentTypeCode }
  | { type: 'SET_PHONE'; value: string }
  | { type: 'SET_MOBILE_PHONE_ERROR'; message: string }
  | { type: 'CLEAR_MOBILE_PHONE_ERROR' };

export const INITIAL_CONSENT_FORM_STATE: ConsentFormState = {
  values: {
    electronicDelivery: false,
    textAlerts: false,
    automatedCalling: false,
    mobilePhone: '',
  },
  mobilePhoneError: null,
};

type ConsentFormActionType = ConsentFormAction['type'];

type ConsentFormActionByType = {
  [T in ConsentFormActionType]: Extract<ConsentFormAction, { type: T }>;
};

type ConsentFormContext = {
  [T in ConsentFormActionType]: (
    state: ConsentFormState,
    action: ConsentFormActionByType[T]
  ) => ConsentFormState;
};

const consentFormContext: ConsentFormContext = {
  TOGGLE: (state, action) => {
    const values = toggleConsentField(state.values, action.consentType);
    const stillNeedsPhone = values.textAlerts || values.automatedCalling;
    return {
      values,
      mobilePhoneError: stillNeedsPhone ? state.mobilePhoneError : null,
    };
  },

  SET_PHONE: (state, action) => ({
    values: { ...state.values, mobilePhone: action.value },
    mobilePhoneError: null,
  }),

  SET_MOBILE_PHONE_ERROR: (state, action) => ({
    ...state,
    mobilePhoneError: action.message,
  }),

  CLEAR_MOBILE_PHONE_ERROR: (state) => ({
    ...state,
    mobilePhoneError: null,
  }),
};

export const consentFormReducer = (
  state: ConsentFormState,
  action: ConsentFormAction
): ConsentFormState => {
  const reduce = consentFormContext[action.type] as (
    current: ConsentFormState,
    next: ConsentFormAction
  ) => ConsentFormState;

  return reduce(state, action);
};
